// CertificadosStreaming.js — «Mis certificados» con el lenguaje del catálogo
// streaming (Learning): banner con las portadas, cifras, los certificados
// obtenidos como diplomas en miniatura y «En camino» con los cursos empezados y
// cuánto falta. La lógica (ver, descargar, encuesta, LinkedIn) la pone
// MisCertificadosPage; aquí solo se pinta.
import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { mentor2Service, BACKEND_BASE } from '../../services/api';
import MuroCursos from '../landing/sections/MuroCursos';
import useMiPlan from '../../hooks/useMiPlan';

const FUENTE = "'Bricolage Grotesque', 'Inter', system-ui, sans-serif";
const urlImg = (v) => (!v ? '' : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const fecha = (d) => { const x = d ? new Date(String(d).replace(' ', 'T')) : new Date(); return `${x.getDate()} de ${MESES[x.getMonth()]} de ${x.getFullYear()}`; };

const P = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
const I = {
  candado: <svg viewBox="0 0 24 24" {...P}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>,
  medalla: <svg viewBox="0 0 24 24" {...P}><circle cx="12" cy="9" r="5.5" /><path d="m9.3 13.8-1.6 7.2L12 18.7l4.3 2.3-1.6-7.2" /><path d="m9.8 9 1.5 1.5 3-3" /></svg>,
  ojo: <svg viewBox="0 0 24 24" {...P}><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="3" /></svg>,
  bajar: <svg viewBox="0 0 24 24" {...P}><path d="M12 4v11" /><path d="m7 10.5 5 5 5-5" /><path d="M5 20h14" /></svg>,
  linkedin: <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9.75h4v11H3v-11Zm6.5 0h3.8v1.5h.06c.53-1 1.83-2.06 3.77-2.06 4.03 0 4.77 2.65 4.77 6.1v5.46h-4v-4.84c0-1.16-.02-2.64-1.6-2.64-1.62 0-1.86 1.26-1.86 2.56v4.92h-4v-11Z" /></svg>,
  escudo: <svg viewBox="0 0 24 24" {...P}><path d="M12 3 5 6v5c0 4.4 3 8.3 7 9.5 4-1.2 7-5.1 7-9.5V6Z" /><path d="m9 12 2.2 2.2L15.5 10" /></svg>,
  play: <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5.5v13l11-6.5Z" /></svg>,
  copiar: <svg viewBox="0 0 24 24" {...P}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V6a2 2 0 0 1 2-2h9" /></svg>,
  check: <svg viewBox="0 0 24 24" {...P}><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>,
  alerta: <svg viewBox="0 0 24 24" {...P}><path d="M9 4h6M10 8h4M8 12h8M7 16h10M6 20h12" /></svg>,
};

const aparecer = keyframes`from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; }`;
const brillo = keyframes`from { transform: translateX(-130%) skewX(-20deg); } to { transform: translateX(330%) skewX(-20deg); }`;

const Raiz = styled.div`
  --acento: ${p => p.$acento};
  background: ${p => p.$fondo}; color: #d5d9df; font-family: ${FUENTE}; min-height: 100vh;
`;
const Cabecera = styled.header`
  position: relative; overflow: hidden; isolation: isolate; min-height: clamp(380px, 52vh, 520px);
  display: flex; flex-direction: column; justify-content: flex-end; padding: clamp(8rem, 12vw, 9rem) 4vw clamp(2rem, 4vw, 3rem);
  .eyebrow { display: inline-flex; align-items: center; gap: .6rem; font-size: .95rem; font-weight: 600; color: #e7eaee; }
  .eyebrow::before { content: ''; width: 28px; height: 2px; background: var(--acento); }
  h1 { margin: .9rem 0 0; font-size: clamp(3rem, 8vw, 6.2rem); font-weight: 800; font-stretch: 76%; line-height: .9; letter-spacing: -.02em; color: #fff; }
`;
const Cifras = styled.dl`
  display: flex; flex-wrap: wrap; gap: .7rem; margin: 1.6rem 0 0;
  div { display: flex; align-items: baseline; gap: .5rem; padding: .6rem 1.1rem; border-radius: 999px;
    background: rgba(0,0,0,.42); box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
  dt { order: 2; font-size: .9rem; color: #c3c9d1; }
  dd { margin: 0; font-size: 1.3rem; font-weight: 800; color: #fff; font-variant-numeric: tabular-nums; }
  div.destacada dd { color: var(--acento); }
`;
const Cuerpo = styled.div`padding: clamp(1.5rem, 3vw, 2.5rem) 4vw clamp(4rem, 7vw, 6rem);`;
const Titulo = styled.h2`
  display: flex; align-items: center; gap: .7rem; margin: 0 0 1.3rem; font-size: clamp(1.4rem, 2.4vw, 1.9rem); font-weight: 800; font-stretch: 85%; color: #fff;
  span { display: inline-grid; place-items: center; min-width: 28px; height: 28px; padding: 0 .5rem; border-radius: 999px; font-size: .85rem; font-stretch: 100%;
    background: rgba(255,255,255,.08); color: #c3c9d1; }
  & + p { margin: -.8rem 0 1.4rem; color: #8a929d; font-size: .95rem; }
`;
const Grilla = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr)); gap: 1.2rem; margin-bottom: 3.5rem;
`;
// Diploma en miniatura: papel oscuro con marco de acento, sello y brillo al pasar.
const Diploma = styled.article`
  position: relative; overflow: hidden; isolation: isolate; display: flex; flex-direction: column; border-radius: 18px;
  background:
    radial-gradient(120% 80% at 100% 0%, color-mix(in srgb, var(--acento) 22%, transparent), transparent 55%),
    linear-gradient(160deg, #222, #161616);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.1), 0 24px 60px -30px rgba(0,0,0,.9);
  animation: ${aparecer} .5s ${p => p.$i * 0.06}s cubic-bezier(.2,.8,.2,1) both;
  transition: transform .25s ease, box-shadow .25s ease;
  &::after { content: ''; position: absolute; inset: 0 auto 0 0; width: 35%; z-index: 2; pointer-events: none;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.12), transparent); transform: translateX(-130%) skewX(-20deg); }
  &:hover { transform: translateY(-4px); box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--acento) 55%, transparent), 0 30px 70px -30px color-mix(in srgb, var(--acento) 60%, #000); }
  &:hover::after { animation: ${brillo} .9s ease-out; }
  .papel { position: relative; flex: 1; display: flex; flex-direction: column; margin: 14px; padding: 1.4rem 1.3rem 1.2rem; border-radius: 12px; text-align: center;
    border: 1px solid color-mix(in srgb, var(--acento) 40%, rgba(255,255,255,.15)); }
  .papel::before { content: ''; position: absolute; inset: 5px; border-radius: 9px; border: 1px dashed rgba(255,255,255,.1); pointer-events: none; }
  .sello { display: grid; place-items: center; width: 54px; height: 54px; margin: 0 auto .8rem; border-radius: 50%; color: #fff;
    background: radial-gradient(circle at 35% 30%, color-mix(in srgb, var(--acento) 65%, #fff), var(--acento) 62%);
    box-shadow: 0 10px 26px -10px var(--acento), inset 0 1px 0 rgba(255,255,255,.35); }
  .sello svg { width: 28px; height: 28px; }
  .tipo { font-size: .72rem; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: #9aa1ab; }
  h3 { margin: .45rem 0 .7rem; font-size: 1.25rem; font-weight: 800; font-stretch: 88%; line-height: 1.15; color: #fff; text-wrap: balance; }
  .para { margin-bottom: 1.1rem; font-size: .88rem; color: #9aa1ab; }
  .para b { display: block; margin-top: .15rem; font-size: 1rem; color: #e7eaee; font-weight: 600; }
  .pie { display: flex; justify-content: space-between; gap: 1rem; margin-top: auto; padding-top: .8rem; margin-bottom: 0; padding-top: .8rem; border-top: 1px solid rgba(255,255,255,.08);
    font-size: .78rem; color: #8a929d; text-align: left; }
  .pie b { display: block; color: #c3c9d1; font-weight: 600; font-size: .82rem; }
  .codigo { display: flex; margin-left: auto; align-items: center; gap: .3rem; border: 0; padding: 0; background: none; font: inherit; color: #c3c9d1; cursor: pointer;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .8rem; font-weight: 600; }
  .codigo svg { width: 13px; height: 13px; color: #8a929d; }
  .codigo:hover, .codigo:hover svg { color: #fff; }
  .codigo:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; border-radius: 3px; }
  .acciones { display: flex; gap: .5rem; padding: 0 14px 14px; margin-top: auto; }
  @media (prefers-reduced-motion: reduce) { animation: none; &:hover::after { animation: none; } }
`;
const Boton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: .45rem; height: 44px; padding: 0 1.1rem; border: 0; border-radius: 999px;
  font: inherit; font-size: .92rem; font-weight: 700; cursor: pointer; text-decoration: none; white-space: nowrap;
  transition: transform .15s ease, background .2s, filter .2s;
  svg { width: 18px; height: 18px; flex: none; }
  ${p => (p.$principal
    ? `flex: 1; background: var(--acento); color: #fff; box-shadow: 0 10px 26px -12px var(--acento); &:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }`
    : `width: 44px; padding: 0; background: rgba(255,255,255,.08); color: #e7eaee; box-shadow: inset 0 0 0 1px rgba(255,255,255,.12); &:hover:not(:disabled) { background: rgba(255,255,255,.16); color: #fff; }`)}
  &:disabled { opacity: .6; cursor: progress; }
  &:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
`;
const Encuesta = styled.div`
  margin: 0 14px 14px; padding: .9rem 1rem; border-radius: 12px; background: rgba(245,196,107,.1); color: #f5d79a; font-size: .88rem; line-height: 1.45;
  button { display: block; width: 100%; margin-top: .7rem; height: 42px; border: 0; border-radius: 999px; background: #f5c46b; color: #1a1406; font: inherit; font-weight: 800; cursor: pointer; }
  button:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
`;
// Curso en camino: portada con anillo de avance y lo que falta.
const Camino = styled.article`
  display: grid; grid-template-columns: 132px 1fr; gap: 1rem; align-items: center; padding: .8rem; border-radius: 16px;
  background: rgba(255,255,255,.035); box-shadow: inset 0 0 0 1px rgba(255,255,255,.08);
  animation: ${aparecer} .5s ${p => p.$i * 0.05}s cubic-bezier(.2,.8,.2,1) both; transition: background .2s, box-shadow .2s;
  &:hover { background: rgba(255,255,255,.06); box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--acento) 45%, transparent); }
  .portada { position: relative; aspect-ratio: 16 / 10; border-radius: 10px; overflow: hidden; background: #222; }
  .portada img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .portada .barra { position: absolute; left: 0; right: 0; bottom: 0; height: 4px; background: rgba(255,255,255,.2); }
  .portada .barra i { display: block; height: 100%; background: var(--acento); }
  h3 { margin: 0 0 .3rem; font-size: 1.02rem; font-weight: 700; line-height: 1.25; color: #fff; }
  .falta { margin: 0 0 .7rem; font-size: .85rem; color: #9aa1ab; }
  .falta b { color: #e7eaee; }
  .fila { display: flex; align-items: center; justify-content: space-between; gap: .8rem; }
  .pct { font-size: 1.15rem; font-weight: 800; color: #fff; font-variant-numeric: tabular-nums; }
  .pct small { font-size: .75rem; font-weight: 600; color: #8a929d; margin-left: .2rem; }
  a { display: inline-flex; align-items: center; gap: .4rem; height: 38px; padding: 0 1rem; border-radius: 999px; text-decoration: none;
    background: #fff; color: #111; font-weight: 700; font-size: .88rem; transition: transform .15s; }
  a svg { width: 14px; height: 14px; }
  a:hover { transform: scale(1.04); }
  a:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  @media (max-width: 480px) { grid-template-columns: 96px 1fr; .fila { flex-wrap: wrap; } }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Tabla = styled.div`
  margin-bottom: 3.5rem; border-radius: 18px; overflow: hidden;
  background: rgba(255,255,255,.03); box-shadow: inset 0 0 0 1px rgba(255,255,255,.08);
  table { width: 100%; border-collapse: collapse; }
  th { padding: 1rem 1.2rem; text-align: left; font-size: .74rem; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: #8a929d;
    background: rgba(0,0,0,.25); border-bottom: 1px solid rgba(255,255,255,.08); }
  .sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
  @media (max-width: 760px) {
    background: none; box-shadow: none; overflow: visible;
    thead { display: none; }
    table, tbody { display: block; }
  }
`;
const Fila = styled.tr`
  position: relative; cursor: pointer; transition: background .2s;
  animation: ${aparecer} .45s ${p => p.$i * 0.05}s cubic-bezier(.2,.8,.2,1) both;
  & + & td { border-top: 1px solid rgba(255,255,255,.06); }
  td { padding: .9rem 1.2rem; vertical-align: middle; color: #c3c9d1; font-size: .92rem; }
  td:first-child { box-shadow: inset 3px 0 0 transparent; transition: box-shadow .2s; }
  &:hover { background: rgba(255,255,255,.045); }
  &:hover td:first-child { box-shadow: inset 3px 0 0 var(--acento); }
  .curso { display: flex; align-items: center; gap: 1rem; min-width: 0; }
  .mini { position: relative; flex: none; width: 104px; aspect-ratio: 16 / 9; border-radius: 8px; overflow: hidden; background: #222; }
  .mini img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .35s; }
  &:hover .mini img { transform: scale(1.08); }
  .mini i { position: absolute; left: 0; bottom: 0; height: 3px; background: var(--acento); }
  strong { color: #fff; font-weight: 700; font-size: .98rem; line-height: 1.25; }
  .avance { display: flex; align-items: center; gap: .8rem; }
  .seg { display: flex; gap: 3px; }
  .seg i { width: 9px; height: 18px; border-radius: 3px; background: rgba(255,255,255,.1); }
  .seg i.on { background: ${p => (p.$listo ? '#3ddc84' : 'var(--acento)')}; box-shadow: 0 0 10px -2px ${p => (p.$listo ? '#3ddc84' : 'var(--acento)')}; }
  .num { font-variant-numeric: tabular-nums; white-space: nowrap; }
  .num b { color: #fff; }
  .accion { text-align: right; }
  .accion a { display: inline-flex; align-items: center; gap: .4rem; height: 38px; padding: 0 1rem; border-radius: 999px; text-decoration: none; white-space: nowrap;
    background: rgba(255,255,255,.08); color: #fff; font-weight: 700; font-size: .86rem; box-shadow: inset 0 0 0 1px rgba(255,255,255,.12); transition: background .2s, color .2s, transform .15s; }
  .accion a svg { width: 13px; height: 13px; }
  &:hover .accion a { background: #fff; color: #111; }
  .accion a:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  @media (max-width: 1000px) { .seg { display: none; } }
  @media (max-width: 760px) {
    display: grid; grid-template-columns: 1fr auto; gap: .2rem 1rem; margin-bottom: .8rem; padding: .9rem; border-radius: 16px;
    background: rgba(255,255,255,.035); box-shadow: inset 0 0 0 1px rgba(255,255,255,.08);
    & + & td { border-top: 0; }
    td { padding: 0; }
    td:first-child { grid-column: 1 / -1; margin-bottom: .7rem; box-shadow: none !important; }
    td[data-col='Avance'] { grid-column: 1; }
    td[data-col='Lecciones'] { grid-column: 2; grid-row: 2; align-self: center; text-align: right; }
    td[data-col='Estado'] { grid-column: 1; margin-top: .6rem; }
    .accion { grid-column: 2; grid-row: 3; margin-top: .6rem; }
    .mini { width: 96px; }
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Anillo = styled.span`
  position: relative; display: grid; place-items: center; flex: none; width: 42px; height: 42px; border-radius: 50%;
  background: conic-gradient(${p => (p.$pct >= 100 ? '#3ddc84' : 'var(--acento)')} ${p => p.$pct * 3.6}deg, rgba(255,255,255,.1) 0);
  &::before { content: ''; position: absolute; inset: 4px; border-radius: 50%; background: #1a1a1a; }
  b { position: relative; font-size: .8rem; font-weight: 800; color: #fff; font-variant-numeric: tabular-nums; }
  b::after { content: '%'; font-size: .6rem; font-weight: 600; color: #8a929d; }
`;
const Estado = styled.span`
  display: inline-flex; align-items: center; gap: .4rem; height: 28px; padding: 0 .75rem; border-radius: 999px; font-size: .8rem; font-weight: 700; white-space: nowrap;
  background: ${p => (p.$listo ? 'rgba(61,220,132,.12)' : 'color-mix(in srgb, var(--acento) 14%, transparent)')};
  color: ${p => (p.$listo ? '#6ee7a8' : '#ff9b9b')};
  &::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: currentColor; box-shadow: 0 0 0 3px color-mix(in srgb, currentColor 25%, transparent); }
`;
const ListaCamino = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 420px), 1fr)); gap: .9rem; margin-bottom: 3.5rem;`;
const latido = keyframes`0%, 100% { transform: scale(1); } 50% { transform: scale(1.06); }`;
const Vacio = styled.div`
  display: grid; grid-template-columns: auto 1fr; gap: 1.6rem 2rem; align-items: center; padding: clamp(1.5rem, 4vw, 2.5rem); margin-bottom: 3rem; border-radius: 22px;
  background: radial-gradient(90% 120% at 0% 0%, color-mix(in srgb, var(--acento) 16%, transparent), transparent 60%), rgba(255,255,255,.03);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.08);
  .medalla { display: grid; place-items: center; width: 96px; height: 96px; border-radius: 50%; color: #fff;
    background: radial-gradient(circle at 35% 30%, color-mix(in srgb, var(--acento) 60%, #fff), var(--acento) 62%);
    box-shadow: 0 20px 50px -18px var(--acento); animation: ${latido} 2.6s ease-in-out infinite; }
  .medalla svg { width: 46px; height: 46px; }
  h2 { margin: 0 0 .4rem; font-size: clamp(1.4rem, 2.6vw, 1.9rem); font-weight: 800; font-stretch: 85%; color: #fff; }
  p { margin: 0 0 1.1rem; color: #b9c0ca; max-width: 36rem; line-height: 1.55; }
  a { display: inline-flex; align-items: center; gap: .5rem; height: 46px; padding: 0 1.3rem; border-radius: 999px; background: var(--acento); color: #fff;
    font-weight: 700; text-decoration: none; box-shadow: 0 12px 30px -12px var(--acento); }
  a svg { width: 15px; height: 15px; }
  a:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
  @media (max-width: 560px) { grid-template-columns: 1fr; text-align: center; .medalla { margin: 0 auto; } p { margin-inline: auto; } }
  @media (prefers-reduced-motion: reduce) { .medalla { animation: none; } }
`;
const Cargando = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr)); gap: 1.2rem;
  div { height: 300px; border-radius: 18px; background: linear-gradient(90deg, rgba(255,255,255,.04), rgba(255,255,255,.08), rgba(255,255,255,.04)); background-size: 200% 100%;
    animation: ${keyframes`to { background-position: -200% 0; }`} 1.4s linear infinite; }
`;

// Plan de formación (alumnos con subrol especial): arriba «Certificado final de cursos
// obligatorios» (bloqueado con lo que falta, o listo) y el resto como «Certificados generales».
const PlanCert = styled.section`
  display: grid; grid-template-columns: 170px minmax(0, 1fr) auto; gap: 1.5rem; align-items: center; margin-bottom: 3rem;
  padding: clamp(1.2rem, 3vw, 1.6rem); border-radius: 14px;
  background: linear-gradient(120deg, color-mix(in srgb, var(--acento) 14%, transparent), rgba(255,255,255,.03) 60%);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--acento) 40%, transparent);
  .papel { position: relative; aspect-ratio: 1.41; border-radius: 4px; background: #f3efe6; display: grid; place-items: center;
    box-shadow: 0 12px 26px -12px #000; }
  .papel::before { content: ''; position: absolute; inset: 8px; border: 1px solid #c8b58a; border-radius: 2px; }
  .papel span.t { font-size: .66rem; font-weight: 800; color: #6b5a2e; letter-spacing: .1em; text-transform: uppercase; filter: blur(.3px); opacity: .5; }
  .candado { position: absolute; display: grid; place-items: center; width: 40px; height: 40px; border-radius: 50%; background: #141414; color: #fff;
    box-shadow: 0 0 0 2px rgba(255,255,255,.2); }
  .candado svg { width: 18px; height: 18px; }
  small { font-size: .85rem; font-weight: 700; color: #ff8a8f; }
  h3 { margin: .3rem 0 0; font-size: 1.4rem; font-weight: 800; font-stretch: 85%; color: #fff; }
  p { margin: .45rem 0 0; color: #c3c9d1; line-height: 1.5; }
  .barra { margin-top: .8rem; height: 6px; max-width: 360px; border-radius: 3px; background: rgba(255,255,255,.12); overflow: hidden; }
  .barra i { display: block; height: 100%; background: var(--acento); }
  a { display: inline-flex; align-items: center; height: 46px; padding: 0 1.2rem; border-radius: 8px; background: #fff; color: #111; font-weight: 700; text-decoration: none; white-space: nowrap; }
  @media (max-width: 720px) { grid-template-columns: 110px minmax(0, 1fr); a { grid-column: 1 / -1; justify-self: start; } }
`;

const CertificadosStreaming = ({
  proyecto, user, cargando, emitidos, enProgreso, busy, encPendiente,
  onVer, onDescargar, onEncuesta, linkedinVisible, linkedinUrl, onLinkedIn, mostrarDescargar,
}) => {
  const navigate = useNavigate();
  const mp = useMiPlan({ detalle: true });
  const enPlan = mp.modo === 'plan';
  const keyPlan = enPlan ? mp.certificado?.key : null;
  const cat = (proyecto?.config_json?.landing_secciones || []).find(s => s.tipo === 'catalogo_streaming')?.config || {};
  const acento = cat.color_acento || '#E50914';
  const fondo = cat.color_fondo || '#141414';
  const docs = proyecto?.documentos;
  const cursos = useMemo(() => (cat.items || []).filter(it => it.documento_id)
    .filter(it => !enPlan || (mp.cursos || []).map(String).includes(String(it.documento_id))).map(it => {
    const d = (docs || []).find(x => String(x.id) === String(it.documento_id));
    return { id: String(it.documento_id), titulo: it.titulo || d?.titulo || '', imagen: urlImg(it.imagen || it.imagen_cartelera || d?.imagen) };
  }), [cat.items, docs, enPlan, mp.cursos]);

  // Avance de cada curso del catálogo, para «En camino».
  const [avance, setAvance] = useState(null);   // { progreso:{id:%}, lecciones:{id:{total,hechas}} }
  const ids = cursos.map(c => c.id).join(',');
  useEffect(() => {
    if (!ids) { setAvance({ progreso: {}, lecciones: {} }); return undefined; }
    let vivo = true;
    mentor2Service.progresoProyecto(ids.split(','))
      .then(r => { if (vivo) setAvance({ progreso: r.data?.progreso || {}, lecciones: r.data?.lecciones || {} }); })
      .catch(() => { if (vivo) setAvance({ progreso: {}, lecciones: {} }); });
    return () => { vivo = false; };
  }, [ids]);

  const [copiado, setCopiado] = useState('');
  const copiar = (codigo) => navigator.clipboard?.writeText(`${window.location.origin}/verificar/${codigo}`)
    .then(() => { setCopiado(codigo); setTimeout(() => setCopiado(''), 1800); }).catch(() => {});

  const enCamino = cursos
    .map(c => ({ ...c, pct: Math.round(Number(avance?.progreso?.[c.id] || 0)), lec: avance?.lecciones?.[c.id] }))
    .filter(c => c.pct > 0)
    .sort((a, b) => b.pct - a.pct);
  const certPlan = keyPlan ? (emitidos || []).find(c => c.certificado_key === keyPlan) : null;
  const listos = (cargando || !avance) ? null : (keyPlan ? emitidos.filter(c => c.certificado_key !== keyPlan) : emitidos);
  const porObtener = keyPlan ? enProgreso.filter(c => c.certificado_key !== keyPlan) : enProgreso;
  const nombre = user?.nombre || user?.name || '';

  const diploma = (c, i) => (
                <Diploma key={c.certificado_id} $i={i}>
                  <div className="papel">
                    <div className="sello">{I.medalla}</div>
                    <div className="tipo">Certificado</div>
                    <h3>{c.titulo || c.curso}</h3>
                    {nombre && <div className="para">Otorgado a<b>{nombre}</b></div>}
                    <div className="pie">
                      <span>Emitido<b>{fecha(c.fecha)}</b></span>
                      {c.codigo && (
                        <span style={{ textAlign: 'right' }}>Verificación
                          <button type="button" className="codigo" onClick={() => copiar(c.codigo)} title="Copiar enlace de verificación"
                            aria-label={`Copiar enlace de verificación ${c.codigo}`}>
                            {c.codigo}{copiado === c.codigo ? I.check : I.copiar}
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                  {encPendiente[c.certificado_id] ? (
                    <Encuesta>
                      Responde una encuesta corta para descargar tu certificado.
                      <button type="button" onClick={() => onEncuesta(c)}>Responder encuesta</button>
                    </Encuesta>
                  ) : (
                    <div className="acciones">
                      <Boton $principal disabled={busy === `ver-${c.certificado_id}`} onClick={() => onVer(c)}>
                        {I.ojo}{busy === `ver-${c.certificado_id}` ? 'Abriendo…' : 'Ver certificado'}
                      </Boton>
                      {mostrarDescargar && (
                        <Boton disabled={busy === `dl-${c.certificado_id}`} onClick={() => onDescargar(c)} aria-label="Descargar PDF" title="Descargar PDF">{I.bajar}</Boton>
                      )}
                      {linkedinVisible && (
                        <Boton as="a" href={linkedinUrl(c)} target="_blank" rel="noopener noreferrer" onClick={e => onLinkedIn(e, c)} aria-label="Compartir en LinkedIn" title="Compartir en LinkedIn">{I.linkedin}</Boton>
                      )}
                      {c.codigo && (
                        <Boton as={Link} to={`/verificar/${c.codigo}`} aria-label="Ver verificación pública" title="Ver verificación pública">{I.escudo}</Boton>
                      )}
                    </div>
                  )}
                </Diploma>
  );

  return (
    <Raiz $acento={acento} $fondo={fondo}>
      <Cabecera>
        <MuroCursos proyecto={proyecto} fondo={fondo} acento={acento} opacidad={0.6} />
        <span className="eyebrow">Tus logros</span>
        <h1>Mis certificados</h1>
        <Cifras>
          <div className="destacada"><dt>{emitidos.length === 1 ? 'certificado' : 'certificados'}</dt><dd>{cargando ? '–' : emitidos.length}</dd></div>
          <div><dt>{enCamino.length === 1 ? 'curso en camino' : 'cursos en camino'}</dt><dd>{avance ? enCamino.filter(c => c.pct < 100).length : '–'}</dd></div>
          <div><dt>{enCamino.filter(c => c.pct >= 100).length === 1 ? 'curso completado' : 'cursos completados'}</dt><dd>{avance ? enCamino.filter(c => c.pct >= 100).length : '–'}</dd></div>
        </Cifras>
      </Cabecera>

      <Cuerpo>
        {enPlan && listos !== null && (
          <>
            <Titulo>Certificado final de cursos obligatorios</Titulo>
            {certPlan ? (
              <Grilla style={{ marginBottom: '3rem' }}>{diploma(certPlan, 0)}</Grilla>
            ) : (
              <PlanCert aria-label="Certificado final del plan">
                <div className="papel"><span className="t">Certificado</span><span className="candado" aria-hidden="true">{I.candado}</span></div>
                <div>
                  <small>Bloqueado</small>
                  <h3>{mp.plan?.nombre || 'Plan de formación'}</h3>
                  <p>{mp.avance?.completo
                    ? 'Completaste tu plan. Tu certificado estará disponible muy pronto.'
                    : `Se libera al completar ${mp.avance?.cursos_total === 1 ? 'el curso' : `los ${mp.avance?.cursos_total ?? ''} cursos`} de tu plan, con sus exámenes aprobados. Llevas el ${mp.avance?.pct ?? 0} %.`}</p>
                  <div className="barra" aria-hidden="true"><i style={{ width: `${mp.avance?.pct || 0}%` }} /></div>
                </div>
                <Link to="/mi-plan">Ver mi plan</Link>
              </PlanCert>
            )}
          </>
        )}
        {listos === null ? (
          <Cargando aria-label="Cargando certificados"><div /><div /><div /></Cargando>
        ) : listos.length > 0 ? (
          <>
            <Titulo>{enPlan ? 'Certificados generales' : 'Obtenidos'} <span>{listos.length}</span></Titulo>
            <Grilla>
              {listos.map((c, i) => diploma(c, i))}
            </Grilla>
          </>
        ) : enPlan ? null : (
          <Vacio>
            <div className="medalla" aria-hidden="true">{I.medalla}</div>
            <div>
              <h2>{enCamino.length ? 'Tu primer certificado está cerca' : 'Tu primer certificado te espera'}</h2>
              <p>{enCamino.length
                ? 'Completa uno de los cursos que ya empezaste y aprueba sus exámenes: el certificado aparece aquí solo, listo para descargar y compartir.'
                : 'Elige un curso, complétalo y aprueba sus exámenes: el certificado aparece aquí solo, listo para descargar y compartir.'}</p>
              {enCamino.length
                ? <Link to={`/mentor/${enCamino.find(c => c.pct < 100)?.id || enCamino[0].id}`}>{I.play}Continuar mi curso</Link>
                : <Link to="/">{I.play}Explorar cursos</Link>}
            </div>
          </Vacio>
        )}

        {enCamino.length > 0 && (
          <>
            <Titulo>Mis cursos <span>{enCamino.length}</span></Titulo>
            <p>Los cursos que tienes en marcha. Al completarlos, su certificado aparece arriba.</p>
            <Tabla>
              <table>
                <thead>
                  <tr><th scope="col">Curso</th><th scope="col">Avance</th><th scope="col">Lecciones</th><th scope="col">Estado</th><th scope="col"><span className="sr">Acción</span></th></tr>
                </thead>
                <tbody>
                  {enCamino.map((c, i) => {
                    const total = c.lec?.total || 0;
                    const hechas = Math.min(total, c.lec?.hechas || 0);
                    const listo = c.pct >= 100;
                    return (
                      <Fila key={c.id} $i={i} $listo={listo} onClick={() => navigate(`/mentor/${c.id}`)}>
                        <td data-col="Curso">
                          <div className="curso">
                            <span className="mini">{c.imagen && <img src={c.imagen} alt="" loading="lazy" />}<i style={{ width: `${c.pct}%` }} /></span>
                            <strong>{c.titulo}</strong>
                          </div>
                        </td>
                        <td data-col="Avance">
                          <div className="avance">
                            <Anillo $pct={c.pct} aria-hidden="true"><b>{c.pct}</b></Anillo>
                            <span className="seg" aria-label={`${c.pct}% completado`}>
                              {Array.from({ length: Math.min(total || 10, 20) }).map((_, k, arr) => (
                                <i key={k} className={(k + 1) / arr.length <= c.pct / 100 + 0.0001 ? 'on' : ''} />
                              ))}
                            </span>
                          </div>
                        </td>
                        <td data-col="Lecciones" className="num">{total ? <><b>{hechas}</b> / {total}</> : '—'}</td>
                        <td data-col="Estado"><Estado $listo={listo}>{listo ? 'Completado' : c.pct >= 75 ? 'Casi listo' : 'En curso'}</Estado></td>
                        <td className="accion">
                          <Link to={`/mentor/${c.id}`} onClick={e => e.stopPropagation()}>{I.play}{listo ? 'Repasar' : 'Continuar'}</Link>
                        </td>
                      </Fila>
                    );
                  })}
                </tbody>
              </table>
            </Tabla>
          </>
        )}

        {porObtener.length > 0 && (
          <>
            <Titulo>Por obtener <span>{porObtener.length}</span></Titulo>
            <ListaCamino>
              {porObtener.map((c, i) => (
                <Camino key={`${c.proyecto_id}-${c.certificado_key}`} $i={i} style={{ gridTemplateColumns: '1fr' }}>
                  <div>
                    <h3>{c.curso}</h3>
                    <p className="falta">{c.pendientes?.length ? <>Falta: <b>{c.pendientes.slice(0, 3).join(' · ')}</b>{c.pendientes.length > 3 ? ` y ${c.pendientes.length - 3} más` : ''}</> : `Te falta ${100 - (c.porcentaje || 0)}%`}</p>
                    <div className="portada" style={{ aspectRatio: 'auto', height: 6, borderRadius: 99 }}><div className="barra" style={{ height: 6 }}><i style={{ width: `${c.porcentaje || 0}%` }} /></div></div>
                  </div>
                </Camino>
              ))}
            </ListaCamino>
          </>
        )}
      </Cuerpo>
    </Raiz>
  );
};

export default CertificadosStreaming;
